// src/pages/PrivacyPolicyPage.tsx
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { Footer } from '../components/landing/Footer';

const PrivacyPolicyPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="container mx-auto max-w-3xl mb-12">
        <div className="mb-8">
          <Button 
            as={Link} 
            to="/" 
            variant="ghost" 
            className="flex items-center"
          >
            <ArrowLeft size={18} className="mr-1" />
            Back to Home
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Privacy Policy</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none">
            <p className="text-sm text-gray-500 mb-8">
              Last updated: {new Date().toLocaleDateString()}
            </p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Introduction</h2>
              <p>
                This Privacy Policy describes how Simplified Trading Journal ("we," "us," or "our") collects, uses, and shares your personal information when you use our service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Information We Collect</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Account Information:</strong> Email address and encrypted password when you create an account
                </li>
                <li>
                  <strong>Trading Data:</strong> Trading models, trade entries, and performance metrics you input
                </li>
                <li>
                  <strong>Usage Data:</strong> How you interact with our service, including features used and time spent
                </li>
                <li>
                  <strong>Payment Information:</strong> When you subscribe to our premium service (processed securely by Stripe)
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">How We Use Your Information</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>To provide and maintain our service</li>
                <li>To process your payments and prevent fraud</li>
                <li>To communicate with you about service updates</li>
                <li>To improve our service based on how it's used</li>
                <li>To protect our legal rights and comply with laws</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Data Security</h2>
              <p>
                We implement appropriate security measures to protect your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Data encryption in transit and at rest</li>
                <li>Regular security assessments</li>
                <li>Secure password storage using industry standards</li>
                <li>Limited access to personal information by employees</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Your Rights</h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Export your data</li>
                <li>Opt out of marketing communications</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy, please contact us at:{' '}
                <span className="text-primary-600 hover:text-primary-500">
                  simplifiedtradingjournal@pm.me
                </span>
              </p>
            </section>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
